import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailWarning, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [state, setState] = useState("working");
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setState("error");
      setError("This link is missing its verification code.");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(async () => {
        setState("done");
        await refresh().catch(() => {});
      })
      .catch((e) => {
        setState("error");
        setError(apiError(e));
      });
  }, [params, refresh]);

  return (
    <div className="min-h-screen nv-grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[460px] nv-rise text-center">
        <div className="flex items-center justify-center gap-2.5 mb-10">
          <div className="w-8 h-8 rounded-lg bg-indigo-500 grid place-items-center shadow-[0_0_24px_-4px_rgba(99,102,241,0.8)]">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight">Propojo</span>
        </div>

        {state === "working" && (
          <div data-testid="verify-working" className="flex flex-col items-center gap-3 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
            <p className="text-sm">Confirming your email…</p>
          </div>
        )}

        {state === "done" && (
          <div data-testid="verify-success">
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 grid place-items-center mx-auto">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-5">Email confirmed</h1>
            <p className="text-sm text-zinc-500 mt-2">
              Your workspace is fully unlocked. Time to build your first proposal.
            </p>
            <Button
              data-testid="verify-continue-btn"
              onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="mt-7 h-11 px-6 bg-indigo-500 hover:bg-indigo-400"
            >
              {user ? "Go to dashboard" : "Sign in"}
            </Button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="verify-error">
            <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/40 grid place-items-center mx-auto">
              <MailWarning className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-5">Link not valid</h1>
            <p className="text-sm text-zinc-500 mt-2">{error}</p>
            <p className="text-sm text-zinc-500 mt-4">
              Sign in and use <span className="text-zinc-300">Resend verification</span> from the banner to get a fresh link.
            </p>
            <Link to="/login">
              <Button data-testid="verify-login-btn" className="mt-7 h-11 px-6 bg-indigo-500 hover:bg-indigo-400">
                Back to sign in
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
